import { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { TableToolbar } from './TableToolbar';
import { StarterKit } from '@tiptap/starter-kit';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import { Highlight } from '@tiptap/extension-highlight';
import { Mention } from '@tiptap/extension-mention';
import { Placeholder } from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  CheckSquare,
  Heading1,
  Heading2,
  Table as TableIcon,
  Highlighter,
  AtSign,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface Mentionable {
  id: string; // 'pv:<uuid>' or 'person:<key>'
  kind: 'vendor' | 'person';
  label: string;
  description?: string;
}

export interface RichBodyProps {
  value: unknown | null;
  editable?: boolean;
  placeholder?: string;
  // Called whenever the doc changes; the parent decides whether to debounce-save.
  onChange?: (json: unknown, plainText: string) => void;
  mentionables?: Mentionable[];
  onMentionClick?: (mentionId: string, kind: 'vendor' | 'person') => void;
  // In read-only mode only: fired when the user toggles a task-list checkbox
  // or otherwise mutates the doc through allowed inline interactions. Lets
  // the parent persist directly without going through edit mode.
  onReadOnlyEdit?: (json: unknown, plainText: string) => void;
  // Force the editor to re-mount when the source step changes — otherwise the
  // editor caches the previous content and the new step's body never appears.
  resetKey?: string;
}

function buildExtensions(placeholder?: string, allowReadOnlyChecks = false) {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
    }),
    TaskList,
    TaskItem.configure({
      nested: true,
      // Returning true permits the visual toggle in read-only mode. Tiptap
      // does NOT update the doc JSON itself; we patch + persist via a
      // separate change-event listener (see useEffect below) so the new
      // state survives a refetch.
      onReadOnlyChecked: allowReadOnlyChecks ? () => true : undefined,
    }),
    Table.configure({ resizable: false }),
    TableRow,
    TableCell,
    TableHeader,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Highlight.configure({ multicolor: true }),
    Placeholder.configure({ placeholder: placeholder ?? 'Add details…' }),
    // Custom mention HTML so we can attach data-id/data-kind for view-mode
    // click handling and CSS theming.
    Mention.configure({
      HTMLAttributes: { class: 'mention-chip' },
      renderHTML: ({ options, node }) => [
        'span',
        {
          ...options.HTMLAttributes,
          'data-id': node.attrs.id,
          'data-kind': node.attrs.kind ?? 'vendor',
        },
        `${options.suggestion.char ?? '@'}${node.attrs.label ?? node.attrs.id}`,
      ],
      // We don't use the built-in @-suggestion popup — too much UX surface.
      // Mentions are inserted via the toolbar `+ Mention` button instead.
      suggestion: { char: '@', allowSpaces: false, items: () => [] },
    }).extend({
      addAttributes() {
        return {
          id: { default: null },
          label: { default: null },
          kind: { default: 'vendor' },
        };
      },
    }),
  ];
}

export function RichBody({
  value,
  editable = false,
  placeholder,
  onChange,
  mentionables,
  onMentionClick,
  onReadOnlyEdit,
  resetKey,
}: RichBodyProps) {
  const editor = useEditor(
    {
      extensions: buildExtensions(placeholder, !!onReadOnlyEdit),
      content: (value as object | null) ?? '',
      editable,
      editorProps: {
        attributes: {
          class: 'focus:outline-none',
        },
      },
      onUpdate: ({ editor }) => {
        onChange?.(editor.getJSON(), editor.getText());
      },
    },
    [editable, resetKey],
  );

  // Reflect external `value` changes in the editor's content. useEditor only
  // takes `content` as initial state, so without this the displayed doc goes
  // stale when the parent's `value` updates (e.g. after an optimistic cache
  // write). Skip when the doc is already equal to avoid clobbering the
  // user's cursor mid-edit.
  useEffect(() => {
    if (!editor) return;
    const incoming = (value as object | null) ?? '';
    const current = editor.getJSON();
    if (JSON.stringify(current) === JSON.stringify(value ?? { type: 'doc', content: [] })) {
      return;
    }
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [editor, value]);

  // View-mode: toggling a checkbox dispatches a `change` event on the input.
  // Tiptap's onReadOnlyChecked already left the visual checkbox flipped; we
  // patch the JSON at the matching task-item index and bubble the new doc to
  // the parent so it lands in the cache + DB.
  useEffect(() => {
    if (!editor || editable || !onReadOnlyEdit) return;
    const handler = (e: Event) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;
      const checkboxes = Array.from(
        editor.view.dom.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'),
      );
      const idx = checkboxes.indexOf(target);
      if (idx === -1) return;
      const patched = patchTaskCheckedAt(value, idx, target.checked);
      if (!patched) return;
      onReadOnlyEdit(patched, flattenDocText(patched));
    };
    const dom = editor.view.dom;
    dom.addEventListener('change', handler);
    return () => dom.removeEventListener('change', handler);
  }, [editor, editable, onReadOnlyEdit, value]);

  // View-mode: clicking a mention chip opens the contact popover.
  useEffect(() => {
    if (!editor) return;
    const handler = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const chip = target.closest('.mention-chip');
      if (!chip) return;
      const id = chip.getAttribute('data-id');
      const kind = (chip.getAttribute('data-kind') ?? 'vendor') as 'vendor' | 'person';
      if (id) onMentionClick?.(id, kind);
    };
    const dom = editor.view.dom;
    dom.addEventListener('click', handler);
    return () => dom.removeEventListener('click', handler);
  }, [editor, onMentionClick]);

  if (!editor) return null;

  return (
    <div className="rich-body">
      {editable && <Toolbar editor={editor} mentionables={mentionables} />}
      <EditorContent editor={editor} className="text-sm" />
      {editable && <TableToolbar editor={editor} />}
    </div>
  );
}

function ToolButton({
  active,
  onClick,
  label,
  icon,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`inline-flex items-center justify-center size-7 rounded text-xs transition-colors ${
        active
          ? 'bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      {icon}
    </button>
  );
}

const HIGHLIGHT_COLORS: { name: string; value: string; swatch: string }[] = [
  { name: 'Yellow', value: 'yellow', swatch: 'rgba(255, 200, 90, 0.55)' },
  { name: 'Orange', value: 'orange', swatch: 'rgba(255, 165, 60, 0.55)' },
  { name: 'Green', value: 'green', swatch: 'rgba(150, 220, 150, 0.55)' },
  { name: 'Blue', value: 'blue', swatch: 'rgba(160, 200, 250, 0.55)' },
  { name: 'Pink', value: 'pink', swatch: 'rgba(250, 180, 200, 0.55)' },
];

function HighlightMenu({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Highlight"
          className={`inline-flex items-center justify-center size-7 rounded text-xs transition-colors ${
            editor.isActive('highlight')
              ? 'bg-accent text-accent-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Highlighter className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[8rem]">
        {HIGHLIGHT_COLORS.map((c) => (
          <DropdownMenuItem
            key={c.value}
            onClick={() => editor.chain().focus().toggleHighlight({ color: c.value }).run()}
          >
            <span
              className="inline-block size-3 rounded-sm mr-2"
              style={{ background: c.swatch }}
            />
            {c.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => editor.chain().focus().unsetHighlight().run()}>
          Remove highlight
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Toolbar({
  editor,
  mentionables,
}: {
  editor: ReturnType<typeof useEditor>;
  mentionables?: Mentionable[];
}) {
  if (!editor) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-0.5 mb-1.5 pb-1.5 border-b border-border/70 no-print"
      data-no-print
    >
      <ToolButton
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        label="Heading"
        icon={<Heading1 className="size-3.5" />}
      />
      <ToolButton
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        label="Subheading"
        icon={<Heading2 className="size-3.5" />}
      />
      <span className="mx-0.5 h-4 w-px bg-border" />
      <ToolButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        label="Bold"
        icon={<Bold className="size-3.5" />}
      />
      <ToolButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        label="Italic"
        icon={<Italic className="size-3.5" />}
      />
      <ToolButton
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        label="Underline"
        icon={<UnderlineIcon className="size-3.5" />}
      />
      <HighlightMenu editor={editor} />
      <span className="mx-0.5 h-4 w-px bg-border" />
      <ToolButton
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        label="Bullet list"
        icon={<List className="size-3.5" />}
      />
      <ToolButton
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        label="Numbered list"
        icon={<ListOrdered className="size-3.5" />}
      />
      <ToolButton
        active={editor.isActive('taskList')}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        label="Checklist"
        icon={<CheckSquare className="size-3.5" />}
      />
      <span className="mx-0.5 h-4 w-px bg-border" />
      <TableMenu editor={editor} />
      <span className="mx-0.5 h-4 w-px bg-border" />
      <ToolButton
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        label="Align left"
        icon={<AlignLeft className="size-3.5" />}
      />
      <ToolButton
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        label="Align center"
        icon={<AlignCenter className="size-3.5" />}
      />
      <ToolButton
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        label="Align right"
        icon={<AlignRight className="size-3.5" />}
      />
      <span className="mx-0.5 h-4 w-px bg-border" />
      <MentionMenu editor={editor} mentionables={mentionables} />
    </div>
  );
}

// Insert-only entry-point. Once a table exists, the floating TableToolbar
// (rendered when the cursor is inside a table) takes over editing.
function TableMenu({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Insert table"
          className="inline-flex items-center justify-center size-7 rounded text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <TableIcon className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[12rem]">
        <DropdownMenuLabel>Insert table</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
        >
          3 × 3 with header
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 4, cols: 4, withHeaderRow: true })
              .run()
          }
        >
          4 × 4 with header
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 2, cols: 2, withHeaderRow: false })
              .run()
          }
        >
          2 × 2 plain
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MentionMenu({
  editor,
  mentionables,
}: {
  editor: ReturnType<typeof useEditor>;
  mentionables?: Mentionable[];
}) {
  const [filter, setFilter] = useState('');
  if (!editor) return null;
  const items = (mentionables ?? []).filter((m) =>
    filter ? m.label.toLowerCase().includes(filter.toLowerCase()) : true,
  );
  return (
    <DropdownMenu onOpenChange={(o) => !o && setFilter('')}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Mention vendor or person"
          className="inline-flex items-center justify-center size-7 rounded text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <AtSign className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[14rem] max-h-72 overflow-y-auto">
        <DropdownMenuLabel>Mention</DropdownMenuLabel>
        <div className="px-2 pb-1">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search…"
            autoFocus
            className="w-full text-xs px-2 py-1 rounded border border-input bg-transparent outline-none focus:border-primary"
          />
        </div>
        {items.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground italic">No matches.</div>
        ) : (
          items.map((m) => (
            <DropdownMenuItem
              key={m.id}
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .insertContent({
                    type: 'mention',
                    attrs: { id: m.id, label: m.label, kind: m.kind },
                  })
                  .insertContent(' ')
                  .run();
              }}
              className="flex items-start gap-2"
            >
              <span className="mt-0.5">{m.kind === 'person' ? '👤' : '✦'}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm">{m.label}</span>
                {m.description && (
                  <span className="block text-[11px] text-muted-foreground truncate">
                    {m.description}
                  </span>
                )}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Doc-JSON helpers (read-only checkbox path) ──────────────────────────────

interface DocNode {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: DocNode[];
  text?: string;
}

// Toggle the `checked` attr on the Nth taskItem in the doc (depth-first,
// document order). Returns a new tree if found, null otherwise. Pure: never
// mutates the input.
function patchTaskCheckedAt(doc: unknown, targetIndex: number, checked: boolean): unknown | null {
  let count = 0;
  let mutated = false;
  function visit(n: DocNode): DocNode {
    if (mutated) return n;
    if (n.type === 'taskItem') {
      if (count === targetIndex) {
        mutated = true;
        return { ...n, attrs: { ...(n.attrs ?? {}), checked } };
      }
      count++;
    }
    if (Array.isArray(n.content)) {
      return { ...n, content: n.content.map(visit) };
    }
    return n;
  }
  if (!doc || typeof doc !== 'object') return null;
  const out = visit(doc as DocNode);
  return mutated ? out : null;
}

// Plain-text projection of a doc tree — used to keep step.bodyText in sync
// when persisting a checkbox toggle without going through the editor's
// getText().
function flattenDocText(doc: unknown): string {
  if (!doc || typeof doc !== 'object') return '';
  const collect = (n: DocNode): string => {
    if (n.text) return n.text;
    if (!n.content) return '';
    const parts = n.content.map(collect);
    const isBlock = ['paragraph', 'heading', 'listItem', 'taskItem'].includes(n.type ?? '');
    return parts.join(isBlock ? ' ' : '');
  };
  return collect(doc as DocNode);
}
